import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Popover, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import PayrollService, {
  ReportEconomics, ReportPerson, ReportSummary, ReportWeek, Reports, describeError,
} from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import StatTile from '../../components/ui/StatTile';
import DataTable, { Column } from '../../components/ui/DataTable';
import { EmptyState, LoadingState } from '../../components/ui/States';
import ReportsPanel from '../reports/ReportsPanel';
import ReportFilters, {
  DEFAULT_PRESET, DateRange, FilterWeek, ReportStatus, presets,
} from './ReportFilters';
import {
  LineChart, Swatch,
} from './reportCharts';
import {
  RatioMath, count, money, percent, ratioPct, shortDate,
} from './payrollUI';

// What the weeks add up to. Every number here is already stored on a
// week or a line; this page only arranges them. The one thing typed in
// here is the employer tax: the real figure from the payroll provider for
// a week, or the rate the weeks without one are estimated at.
//
// The filters are a bar under the title (ReportFilters). The date range
// asks the server for weeks; status, drivers and picked weeks narrow what
// came back, here, without another request.

const cents = (n: number) => Math.round(n * 100) / 100;
const per = (amount: number, n: number) => (n ? cents(amount / n) : null);
// How heavy the stops are: packages delivered per stop, "1.64".
const perStop = (packages: number, stops: number) => (stops ? (packages / stops).toFixed(2) : '—');
const pct = (part: number, whole: number) => (whole ? cents((part / whole) * 100) : null);

// The same arithmetic as the server's, so the weeks picked here add up
// exactly as the whole range does there.
function economics(x: { gross: number; employerTax: number | null; revenue: number; deductions?: number; stops: number; packages: number; dispatches: number }): ReportEconomics {
  const laborCost = cents(x.gross + (x.employerTax || 0));
  return {
    laborCost,
    margin: cents(x.revenue + (x.deductions || 0) - laborCost),
    ratio: pct(x.gross, x.revenue),
    laborRatio: pct(laborCost, x.revenue),
    costPerStop: per(laborCost, x.stops),
    revenuePerStop: per(x.revenue, x.stops),
    costPerPackage: per(laborCost, x.packages),
    revenuePerPackage: per(x.revenue, x.packages),
    costPerDispatch: per(laborCost, x.dispatches),
    revenuePerDispatch: per(x.revenue, x.dispatches),
  };
}

function summarize(weeks: ReportWeek[]): ReportSummary {
  const sum = (key: 'gross' | 'netAmountDue' | 'revenue' | 'deductions' | 'employerTax' | 'stops' | 'packages' | 'dispatches') => weeks.reduce((t, w) => t + (w[key] || 0), 0);
  const gross = cents(sum('gross'));
  const revenue = cents(sum('revenue'));
  const deductions = cents(sum('deductions'));
  const taxed = weeks.filter((w) => w.employerTax !== null).length;
  const employerTax = taxed ? cents(sum('employerTax')) : null;
  const stops = sum('stops');
  const packages = sum('packages');
  const dispatches = sum('dispatches');
  return {
    weeks: weeks.length,
    gross,
    netAmountDue: cents(sum('netAmountDue')),
    revenue,
    deductions,
    employerTax,
    employerTaxEstimatedWeeks: weeks.filter((w) => w.employerTaxSource === 'estimate').length,
    employerTaxMissingWeeks: weeks.length - taxed,
    averageGross: weeks.length ? cents(gross / weeks.length) : 0,
    stops,
    packages,
    dispatches,
    ...economics({ gross, employerTax, revenue, deductions, stops, packages, dispatches }),
  };
}

// A number typed into a small popover: the week's employer tax, or the
// rate the rest are estimated at. Empty and "clear" both mean null.
const NumberPopover: React.FC<{
  anchor: HTMLElement | null;
  label: string;
  hint: string;
  initial: number | null;
  clearLabel: string;
  onClose: () => void;
  onSave: (value: number | null) => Promise<void>;
}> = ({ anchor, label, hint, initial, clearLabel, onClose, onSave }) => {
  const [text, setText] = useState(initial === null ? '' : String(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setText(initial === null ? '' : String(initial)); setError(null); }, [anchor, initial]);
  const save = async (value: number | null) => {
    setBusy(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      setError(describeError(err, 'Could not save that.'));
    } finally {
      setBusy(false);
    }
  };
  const parsed = text.trim() === '' ? null : Number(text.replace(/[$,%\s]/g, ''));
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0);
  return (
    <Popover open={!!anchor} anchorEl={anchor} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Stack component="form" gap={1.25} sx={{ p: 2, width: 280 }} onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (valid) save(parsed); }}>
        <TextField autoFocus size="small" label={label} value={text} onChange={(e) => setText(e.target.value)} inputProps={{ inputMode: 'decimal' }} error={!valid} />
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
        {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
        <Stack direction="row" justifyContent="space-between">
          <Button size="small" disabled={busy || initial === null} onClick={() => save(null)}>{clearLabel}</Button>
          <Button size="small" variant="contained" disableElevation type="submit" disabled={busy || !valid}>Save</Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

// The weeks as if only these drivers had worked them. Pay, stops, packages
// and dispatches are theirs exactly; revenue is the statement's, shared out
// by their part of the week's stops (FedEx pays by the stop and package,
// so stops are the fair split); employer tax follows their part of payroll.
function forDrivers(weeks: ReportWeek[], people: ReportPerson[], chosen: Set<string>): ReportWeek[] {
  return weeks.map((w) => {
    let pay = 0; let stops = 0; let packages = 0; let days = 0; let allStops = 0; let headcount = 0;
    people.forEach((p) => p.series.forEach((r) => {
      if (r.weekEnd !== w.weekEnd) return;
      allStops += r.stops;
      if (!chosen.has(p.accountId)) return;
      pay += r.pay; stops += r.stops; packages += r.packages || 0; days += r.days; headcount += 1;
    }));
    const gross = cents(pay);
    const share = (n: number) => (allStops ? cents((n * stops) / allStops) : 0);
    const revenue = share(w.revenue);
    const deductions = share(w.deductions);
    const employerTax = w.employerTax === null ? null : (w.gross ? cents((w.employerTax * gross) / w.gross) : 0);
    return {
      ...w,
      gross,
      calculated: gross,
      adjustments: 0,
      netAmountDue: cents(revenue + deductions),
      totalCharges: revenue,
      revenue,
      deductions,
      employerTax,
      dispatches: days,
      stops,
      packages,
      people: headcount,
      ...economics({
        gross, employerTax, revenue, deductions, stops, packages, dispatches: days,
      }),
    };
  });
}

const defaultRangeOf = (all: FilterWeek[]): DateRange | null => presets(all).find((p) => p.key === DEFAULT_PRESET)?.range || null;
// The server returns at most this many weeks for a range.
const MAX_WEEKS = 52;

const UNIT_TABS = [
  { key: 'labor', label: 'Revenue vs payroll + tax' },
  { key: 'dispatch', label: 'Dispatches' },
  { key: 'stop', label: 'Per stop' },
  { key: 'package', label: 'Per package' },
] as const;
type UnitTab = typeof UNIT_TABS[number]['key'];

const PayrollReports: React.FC = () => {
  const navigate = useNavigate();
  // Every uploaded week, oldest first: what the period slider ranges over.
  const [allWeeks, setAllWeeks] = useState<FilterWeek[] | null>(null);
  const [range, setRange] = useState<DateRange | null>(null);
  const [data, setData] = useState<Reports | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Weeks picked off the charts or the week list. Empty means all of them.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [drivers, setDrivers] = useState<string[]>([]);
  const [status, setStatus] = useState<ReportStatus>('all');
  const [tab, setTab] = useState<UnitTab>('labor');
  const [taxEdit, setTaxEdit] = useState<{ week: ReportWeek; anchor: HTMLElement } | null>(null);
  const [rateAnchor, setRateAnchor] = useState<HTMLElement | null>(null);

  const loadWeeks = useCallback(async () => {
    try {
      const runs = await PayrollService.runs();
      const asc = runs
        .map((r) => ({ weekStart: r.weekStart, weekEnd: r.weekEnd, status: r.status }))
        .sort((x, y) => (x.weekEnd < y.weekEnd ? -1 : 1));
      setAllWeeks(asc);
      // The default preset, unless a range is already chosen.
      setRange((r) => r || defaultRangeOf(asc));
      if (!asc.length) setLoading(false);
    } catch (err) {
      setError(describeError(err, 'Could not load the weeks.'));
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadWeeks(); }, [loadWeeks]);

  const rangeKey = range ? `${range.from}|${range.to}` : '';
  const load = useCallback(async () => {
    if (!rangeKey) return;
    const [from, to] = rangeKey.split('|');
    setLoading(true);
    setError(null);
    try {
      setData(await PayrollService.reports({ from, to }));
    } catch (err) {
      setError(describeError(err, 'Could not load the reports.'));
    } finally {
      setLoading(false);
    }
  }, [rangeKey]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPicked(new Set()); }, [rangeKey]);

  const pick = (key: string) => setPicked((p) => {
    const next = new Set(p);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const defaultRange = allWeeks ? defaultRangeOf(allWeeks) : null;
  const filtered = picked.size > 0 || drivers.length > 0 || status !== 'all'
    || (!!range && !!defaultRange && (range.from !== defaultRange.from || range.to !== defaultRange.to));
  const reset = () => {
    if (defaultRange) setRange(defaultRange);
    setPicked(new Set());
    setDrivers([]);
    setStatus('all');
  };

  // Period (from the server) → status → drivers → picked weeks. The charts
  // show the weeks up to the driver step, with picked weeks highlighted;
  // the tiles, tables and people answer for the picked weeks only.
  const view = useMemo(() => {
    if (!data || !data.summary) return null;
    const statusWeeks = status === 'all' ? data.weeks : data.weeks.filter((w) => w.status === status);
    const chosen = new Set(drivers);
    const chartWeeks = drivers.length ? forDrivers(statusWeeks, data.people, chosen) : statusWeeks;
    const weeksIn = picked.size ? chartWeeks.filter((w) => picked.has(w.weekEnd)) : chartWeeks;
    const narrowed = picked.size > 0 || drivers.length > 0 || status !== 'all';
    const summary = !weeksIn.length ? null : narrowed ? summarize(weeksIn) : data.summary;
    const keys = new Set(weeksIn.map((w) => w.weekEnd));
    const people = data.people.filter((p) => !drivers.length || chosen.has(p.accountId)).map((p) => {
      const rows = p.series.filter((w) => keys.has(w.weekEnd));
      const d = rows.reduce((t, w) => t + w.days, 0);
      const st = rows.reduce((t, w) => t + w.stops, 0);
      const pay = cents(rows.reduce((t, w) => t + w.pay, 0));
      return {
        ...p,
        weeks: rows.length,
        days: d,
        stops: st,
        packages: rows.reduce((t, w) => t + (w.packages || 0), 0),
        pay,
        stopsPerDay: d ? Math.round(st / d) : null,
        payPerStop: st ? cents(pay / st) : null,
        payPerDay: d ? cents(pay / d) : null,
      };
    }).filter((p) => p.weeks > 0);
    // Hollow and faded mean "still a draft" only beside weeks that are not.
    const allDraft = chartWeeks.every((w) => w.status === 'draft');
    return {
      summary, people, weeksIn, chartWeeks, allDraft,
    };
  }, [data, picked, drivers, status]);

  // Every number sorts and filters from its header; the name searches.
  const columns: Column<ReportPerson>[] = [
    {
      key: 'person',
      header: 'Driver',
      sortValue: (p) => p.name,
      filter: 'text',
      render: (p) => <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit' }}>{p.name}</Typography>,
    },
    { key: 'days', header: 'Days', align: 'right', numeric: true, hide: 'phone', sortValue: (p) => p.days, render: (p) => p.days },
    { key: 'stops', header: 'Stops', align: 'right', numeric: true, hide: 'tablet', sortValue: (p) => p.stops, render: (p) => count(p.stops) },
    { key: 'spd', header: 'Stops/day', align: 'right', numeric: true, sortValue: (p) => p.stopsPerDay, render: (p) => count(p.stopsPerDay) },
    { key: 'packages', header: 'Packages', align: 'right', numeric: true, hide: 'tablet', sortValue: (p) => p.packages, render: (p) => count(p.packages) },
    { key: 'pkgps', header: 'Pkg/stop', align: 'right', numeric: true, hide: 'tablet', sortValue: (p) => (p.stops ? p.packages / p.stops : null), render: (p) => perStop(p.packages, p.stops) },
    { key: 'pay', header: 'Pay', align: 'right', numeric: true, sortValue: (p) => p.pay, render: (p) => <strong>{money(p.pay)}</strong> },
    { key: 'pps', header: 'Per stop', align: 'right', numeric: true, hide: 'phone', sortValue: (p) => p.payPerStop, render: (p) => (p.payPerStop === null ? '—' : money(p.payPerStop, { cents: true })) },
    { key: 'ppd', header: 'Per day', align: 'right', numeric: true, hide: 'tablet', sortValue: (p) => p.payPerDay, render: (p) => (p.payPerDay === null ? '—' : money(p.payPerDay)) },
  ];

  // The week tables. One week column, then whichever pair of numbers the
  // tab is about; the totals row is the summary for the same weeks.
  const unit = (n: number | null) => (n === null ? '—' : money(n, { cents: true }));
  const dollars = (n: number) => money(Math.round(n));
  const signedDollars = (n: number) => <Box component="span" sx={{ color: n < 0 ? 'error.main' : undefined }}>{dollars(n)}</Box>;
  const signed = (n: number | null) => (n === null ? '—' : (
    <Box component="span" sx={{ color: n < 0 ? 'error.main' : undefined }}>{money(n, { cents: true })}</Box>
  ));
  const weekColumn: Column<ReportWeek> = {
    key: 'week',
    header: 'Week',
    sortValue: (w) => w.weekEnd,
    render: (w) => (
      <Stack direction="row" alignItems="center" gap={0.75}>
        <span>{shortDate(w.weekStart)} – {shortDate(w.weekEnd)}</span>
        {w.status === 'draft' && <Typography variant="caption" color="text.secondary">draft</Typography>}
      </Stack>
    ),
  };
  const num = (key: string, header: string, value: (w: ReportWeek) => number | null, render: (w: ReportWeek) => React.ReactNode, hide?: 'phone' | 'tablet'): Column<ReportWeek> => ({
    key, header, align: 'right', numeric: true, hide, sortValue: value, render,
  });
  const taxCell = (w: ReportWeek) => (drivers.length ? (
    <Tooltip title="These drivers' share of the week's payroll tax. Clear the driver filter to change the week's figure.">
      <span>
        {w.employerTax === null ? '—' : money(Math.round(w.employerTax))}
        {w.employerTaxSource === 'estimate' && <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontSize: '0.75em' }}>est.</Box>}
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={w.employerTaxSource === 'actual' ? 'From Netchex. Click to change' : w.employerTaxSource === 'estimate' ? `Estimated at ${data?.employerTaxRate}% of payroll. Click to enter the Netchex figure` : 'Click to enter the Netchex figure'}>
      <Box
        component="button"
        type="button"
        aria-label={`Payroll tax for the week ending ${shortDate(w.weekEnd)}`}
        onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setTaxEdit({ week: w, anchor: e.currentTarget }); }}
        sx={{
          p: 0, border: 0, bgcolor: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer',
          borderBottom: '1px dashed', borderColor: 'text.disabled', '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
        }}
      >
        {w.employerTax === null ? 'Add' : money(Math.round(w.employerTax))}
        {w.employerTaxSource === 'estimate' && <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontSize: '0.75em' }}>est.</Box>}
      </Box>
    </Tooltip>
  ));
  // A company FedEx deducts nothing from never sees the column.
  const hasDeductions = (view?.weeksIn || []).some((w) => w.deductions);
  const unitColumns: Record<UnitTab, Column<ReportWeek>[]> = {
    labor: [
      weekColumn,
      num('revenue', 'Revenue', (w) => w.revenue, (w) => dollars(w.revenue)),
      ...(hasDeductions ? [num('deductions', 'FedEx deductions', (w) => w.deductions, (w) => (w.deductions ? signedDollars(w.deductions) : '—'), 'tablet')] : []),
      num('gross', 'Payroll', (w) => w.gross, (w) => dollars(w.gross), 'phone'),
      num('tax', 'Tax', (w) => w.employerTax, taxCell, 'phone'),
      num('labor', 'Total cost', (w) => w.laborCost, (w) => <strong>{dollars(w.laborCost)}</strong>),
      num('margin', 'Left over', (w) => w.margin, (w) => signedDollars(w.margin), 'tablet'),
      num('laborRatio', 'Cost %', (w) => w.laborRatio, (w) => percent(w.laborRatio)),
    ],
    dispatch: [
      weekColumn,
      num('dispatches', 'Dispatches', (w) => w.dispatches, (w) => count(w.dispatches)),
      num('revenue', 'Revenue', (w) => w.revenue, (w) => money(w.revenue), 'phone'),
      num('rpd', 'Revenue per dispatch', (w) => w.revenuePerDispatch, (w) => <strong>{unit(w.revenuePerDispatch)}</strong>),
      num('cpd', 'Cost per dispatch', (w) => w.costPerDispatch, (w) => unit(w.costPerDispatch), 'phone'),
    ],
    stop: [
      weekColumn,
      num('stops', 'Stops', (w) => w.stops, (w) => count(w.stops), 'phone'),
      num('cps', 'Cost per stop', (w) => w.costPerStop, (w) => unit(w.costPerStop)),
      num('rps', 'Revenue per stop', (w) => w.revenuePerStop, (w) => unit(w.revenuePerStop)),
      num('mps', 'Left per stop', (w) => (w.revenuePerStop === null || w.costPerStop === null ? null : cents(w.revenuePerStop - w.costPerStop)), (w) => signed(w.revenuePerStop === null || w.costPerStop === null ? null : cents(w.revenuePerStop - w.costPerStop)), 'phone'),
    ],
    package: [
      weekColumn,
      num('packages', 'Packages', (w) => w.packages, (w) => count(w.packages), 'phone'),
      num('pkgps', 'Packages per stop', (w) => (w.stops ? w.packages / w.stops : null), (w) => perStop(w.packages, w.stops)),
      num('cpp', 'Cost per package', (w) => w.costPerPackage, (w) => unit(w.costPerPackage)),
      num('rpp', 'Revenue per package', (w) => w.revenuePerPackage, (w) => unit(w.revenuePerPackage)),
      num('mpp', 'Left per package', (w) => (w.revenuePerPackage === null || w.costPerPackage === null ? null : cents(w.revenuePerPackage - w.costPerPackage)), (w) => signed(w.revenuePerPackage === null || w.costPerPackage === null ? null : cents(w.revenuePerPackage - w.costPerPackage)), 'phone'),
    ],
  };
  const s = view?.summary || null;
  const less = (a: number | null, b: number | null) => (a === null || b === null ? null : cents(a - b));
  const unitTotals: Record<UnitTab, Partial<Record<string, React.ReactNode>>> = !s ? { labor: {}, dispatch: {}, stop: {}, package: {} } : {
    labor: {
      week: 'Total', revenue: dollars(s.revenue), deductions: s.deductions ? signedDollars(s.deductions) : '—', gross: dollars(s.gross), tax: s.employerTax === null ? '—' : dollars(s.employerTax), labor: dollars(s.laborCost), margin: signedDollars(s.margin), laborRatio: percent(s.laborRatio),
    },
    dispatch: {
      week: 'Total', dispatches: count(s.dispatches), revenue: money(s.revenue), rpd: unit(s.revenuePerDispatch), cpd: unit(s.costPerDispatch),
    },
    stop: {
      week: 'Total', stops: count(s.stops), cps: unit(s.costPerStop), rps: unit(s.revenuePerStop), mps: signed(less(s.revenuePerStop, s.costPerStop)),
    },
    package: {
      week: 'Total', packages: count(s.packages), pkgps: perStop(s.packages, s.stops), cpp: unit(s.costPerPackage), rpp: unit(s.revenuePerPackage), mpp: signed(less(s.revenuePerPackage, s.costPerPackage)),
    },
  };

  const drafts = view?.weeksIn.filter((w) => w.status === 'draft').length || 0;
  const weeks = view?.chartWeeks || [];
  const barItems = weeks.map((w) => ({
    key: w.weekEnd, label: shortDate(w.weekEnd), draft: w.status === 'draft' && !view?.allDraft, selected: picked.has(w.weekEnd), dim: picked.size > 0 && !picked.has(w.weekEnd),
  }));
  const peopleList = useMemo(() => (data?.people || []).map((p) => ({ accountId: p.accountId, name: p.name })).sort((x, y) => x.name.localeCompare(y.name)), [data]);
  const lineWeeks = barItems.map(({ key, label, draft, selected, dim }) => ({ key, label, draft, selected, dim }));
  const rate = data?.employerTaxRate ?? null;

  const saveTax = async (value: number | null) => {
    if (!taxEdit) return;
    await PayrollService.setEmployerTax(taxEdit.week.runId, value);
    await load();
  };
  const saveRate = async (value: number | null) => {
    await PayrollService.saveEmployerTaxRate(value);
    await load();
  };

  return (
    <PageBody fluid>
      <ReportsPanel />
      <PageHeader
        title="Payroll & revenue"
        subtitle={s && view?.weeksIn.length ? `${shortDate(view.weeksIn[0].weekStart)} to ${shortDate(view.weeksIn[view.weeksIn.length - 1].weekEnd)}, ${view.weeksIn.length} ${view.weeksIn.length === 1 ? 'week' : 'weeks'}${drafts ? `, ${drafts} in draft` : ''}${drivers.length ? `, ${drivers.length} ${drivers.length === 1 ? 'driver' : 'drivers'}` : ''}` : undefined}
      >
        <ReportFilters
          all={allWeeks || []}
          range={range}
          onRange={setRange}
          inRange={(data?.weeks || []).map((w) => ({ weekStart: w.weekStart, weekEnd: w.weekEnd, status: w.status }))}
          picked={picked}
          onPicked={setPicked}
          people={peopleList}
          drivers={drivers}
          onDrivers={setDrivers}
          status={status}
          onStatus={setStatus}
          onReset={reset}
          canReset={filtered}
        />
      </PageHeader>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {data?.range?.truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>That range has more than {MAX_WEEKS} weeks; these are the newest {MAX_WEEKS}.</Alert>
      )}
      {loading && !data ? <LoadingState /> : !allWeeks?.length || !data || !view ? (
        <EmptyState title={allWeeks?.length ? 'No weeks in this period' : 'Nothing to report yet'} description={allWeeks?.length ? undefined : 'Upload a weekly statement and its numbers show up here.'} />
      ) : !s ? (
        <EmptyState title="No weeks match these filters" action={{ label: 'Clear filters', onClick: reset }} />
      ) : (
        <Stack gap={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            <StatTile label="Payroll" value={money(s.gross)} hint={`${money(s.averageGross)} a week`} />
            <RatioMath
              rows={[
                { label: 'Payroll', amount: s.gross, over: 'revenue before deductions', base: s.revenue },
                ...(s.employerTax === null ? [] : [{ label: 'Payroll + payroll tax', amount: s.laborCost, over: 'revenue before deductions', base: s.revenue }]),
              ]}
            >
              <StatTile label="Payroll ratio" value={percent(ratioPct(s.gross, s.revenue))} hint={s.employerTax === null ? undefined : `${percent(ratioPct(s.laborCost, s.revenue))} with tax`} />
            </RatioMath>
            <StatTile label="Cost per stop" value={s.costPerStop === null ? null : money(s.costPerStop, { cents: true })} hint={`${count(s.stops)} stops · ${perStop(s.packages, s.stops)} packages each`} />
            <StatTile label="Cost per package" value={s.costPerPackage === null ? null : money(s.costPerPackage, { cents: true })} hint={`${count(s.packages)} packages · ${s.revenuePerPackage === null ? '—' : money(s.revenuePerPackage, { cents: true })} revenue each`} />
            <StatTile label="Revenue per dispatch" value={s.revenuePerDispatch === null ? null : money(Math.round(s.revenuePerDispatch))} hint={`${count(s.dispatches)} dispatches`} />
          </Box>

          <LineChart
            title="Revenue vs payroll + tax"
            weeks={lineWeeks}
            series={[
              { series: 'revenue', label: 'revenue', values: weeks.map((w) => w.revenue) },
              { series: 'payroll', label: 'payroll + payroll tax', values: weeks.map((w) => w.laborCost) },
            ]}
            format={(n) => money(Math.round(n))}
            zero
            height={240}
            onPick={pick}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, '& > *': { minWidth: 0 } }}>
            <LineChart
              title="Payroll ratio"
              weeks={lineWeeks}
              series={[
                { series: 'payroll', label: 'payroll', values: weeks.map((w) => w.ratio) },
                ...(weeks.some((w) => w.employerTax !== null) ? [{ series: 'tax' as const, label: 'with tax', values: weeks.map((w) => w.laborRatio) }] : []),
              ]}
              format={(n) => `${Math.round(n * 10) / 10}%`}
              onPick={pick}
            />
            {/* A count and a dollar amount: two rows sharing one set of week
                slots, each on its own scale. On one shared axis the dispatch
                line goes flat, however much it moves. */}
            <Card sx={{ p: 2.5, overflow: 'visible' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: 700 }}>Dispatches and revenue per dispatch</Typography>
                <Typography variant="caption" color="text.secondary">a dispatch is one driver&apos;s day</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" sx={{ mt: 1.5 }}><Swatch series="payroll" line /><Typography variant="caption" color="text.secondary">dispatches</Typography></Stack>
              <LineChart
                bare
                xLabels={false}
                height={96}
                weeks={lineWeeks}
                series={[{ series: 'payroll', label: 'dispatches', values: weeks.map((w) => w.dispatches) }]}
                format={(n) => count(Math.round(n))}
                onPick={pick}
              />
              <Stack direction="row" alignItems="center" sx={{ mt: 1 }}><Swatch series="revenue" line /><Typography variant="caption" color="text.secondary">revenue per dispatch</Typography></Stack>
              <LineChart
                bare
                xLabels
                height={104}
                weeks={lineWeeks}
                series={[{ series: 'revenue', label: 'revenue per dispatch', values: weeks.map((w) => w.revenuePerDispatch) }]}
                format={(n) => money(Math.round(n))}
                onPick={pick}
              />
            </Card>
            <LineChart
              title="Cost vs revenue per stop"
              weeks={lineWeeks}
              series={[
                { series: 'payroll', label: 'cost (payroll + tax)', values: weeks.map((w) => w.costPerStop) },
                { series: 'revenue', label: 'revenue', values: weeks.map((w) => w.revenuePerStop) },
              ]}
              format={(n) => money(n, { cents: true })}
              zero
              onPick={pick}
            />
            <LineChart
              title="Cost vs revenue per package"
              weeks={lineWeeks}
              series={[
                { series: 'payroll', label: 'cost (payroll + tax)', values: weeks.map((w) => w.costPerPackage) },
                { series: 'revenue', label: 'revenue', values: weeks.map((w) => w.revenuePerPackage) },
              ]}
              format={(n) => money(n, { cents: true })}
              zero
              onPick={pick}
            />
            {/* How heavy the stops are, week by week. Full width: it is the
                fifth chart, and a half-width one would leave a hole. */}
            <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
              <LineChart
                title="Packages per stop"
                weeks={lineWeeks}
                series={[{ series: 'payroll', label: 'packages per stop', values: weeks.map((w) => (w.stops ? w.packages / w.stops : null)) }]}
                format={(n) => n.toFixed(2)}
                height={160}
                onPick={pick}
              />
            </Box>
          </Box>
          {/* The two tables share the row and fill it, at any width; on a
              narrow screen they stack. */}
          <Box
            sx={{
              display: 'grid',
              // Side by side once each half can hold its table (~700px).
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 700px), 1fr))',
              gap: 2,
              alignItems: 'start',
              '& > *': { minWidth: 0 },
              // Same type as every table; only the side padding is tighter.
              '& .MuiTableCell-root': { px: 0.75 },
              // Only the sorted column keeps room for its arrow.
              '& .MuiTableSortLabel-root:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' },
              '& .MuiTableCell-root:first-of-type': { pl: 2 },
              '& .MuiTableCell-root:last-of-type': { pr: 2 },
            }}
          >
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1, minHeight: 36 }}>
                <Tabs aria-label="Week by week" value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, px: 1.5 } }}>
                  {UNIT_TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
                </Tabs>
                {/* The table's one editable number, summed up beside it. */}
                <Stack direction="row" alignItems="center" gap={1}>
                  <Tooltip title="Click a week's Tax to type what Netchex charged. Weeks without it use the estimate; weeks with neither count payroll only.">
                    <Typography variant="caption" color={s.employerTaxMissingWeeks ? 'warning.main' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
                      Payroll tax: {view.weeksIn.filter((w) => w.employerTaxSource === 'actual').length} of {s.weeks} from Netchex
                      {' · '}
                      {rate === null ? 'no estimate' : `${s.employerTaxEstimatedWeeks} estimated at ${rate}%`}
                    </Typography>
                  </Tooltip>
                  <Button size="small" onClick={(e) => setRateAnchor(e.currentTarget)} sx={{ whiteSpace: 'nowrap', minWidth: 0 }}>
                    {rate === null ? 'Set estimate' : 'Change'}
                  </Button>
                </Stack>
              </Stack>
              <DataTable
                key={tab}
                columns={unitColumns[tab]}
                rows={view.weeksIn}
                rowKey={(w) => w.runId}
                defaultSort={{ key: 'week', dir: 'desc' }}
                onRowClick={(w) => navigate(`/payroll/weeks/${w.runId}`)}
                totals={unitTotals[tab]}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 0.5 }}>
                Revenue is the statement&apos;s net amount due; cost is payroll plus payroll tax; stops and packages are the statement&apos;s totals.
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" sx={{ mb: 1, minHeight: 36 }}>
                <Typography sx={{ fontWeight: 700 }}>By person</Typography>
              </Stack>
              <DataTable
                columns={columns}
                rows={view.people}
                rowKey={(p) => p.accountId}
                defaultSort={{ key: 'pay', dir: 'desc' }}
                onRowClick={(p) => navigate(`/payroll/people/${p.accountId}`)}
                empty={<EmptyState title="Nobody paid in this range" compact />}
              />
            </Box>
          </Box>
        </Stack>
      )}

      <NumberPopover
        anchor={taxEdit?.anchor || null}
        label="Employer taxes from Netchex"
        hint={`What Netchex charged in employer taxes for the week ending ${taxEdit ? shortDate(taxEdit.week.weekEnd) : ''}: FICA, FUTA and state unemployment. Not the employees' withholding.`}
        initial={taxEdit?.week.employerTaxSource === 'actual' ? taxEdit.week.employerTax : null}
        clearLabel="Use the estimate"
        onClose={() => setTaxEdit(null)}
        onSave={saveTax}
      />
      <NumberPopover
        anchor={rateAnchor}
        label="Payroll tax estimate, % of payroll"
        hint="Used for weeks without a Netchex figure. FICA alone is 7.65%; federal and state unemployment add more early in the year."
        initial={rate}
        clearLabel="Don't estimate"
        onClose={() => setRateAnchor(null)}
        onSave={saveRate}
      />
    </PageBody>
  );
};

export default PayrollReports;
